import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule,ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle,MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { TSurveyResult, TSurveyResultsResponse } from '@core/interfaces/Endpoints';

import { CommonService } from '@shared/services/common.service';
import { ExitSurveyService } from '@shared/services/exit-survey.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';

export interface SurveyQuestion {
  id: string;
  question: string;
}

@Component({
    selector: 'app-exit-survey',
    templateUrl: './exit-survey.component.html',
    styleUrls: ['./exit-survey.component.scss'],
    standalone: true,
    imports: [
      MatSlideToggle,
      MatFormField,
      MatInput,
      ReactiveFormsModule,
      FormsModule,
      MatHint,
      MatButton,
      MatIconButton,
      MatTooltip,
      MatIcon,
      MatProgressSpinner,
      DatePipe,
    ],
})
export class ExitSurveyComponent implements OnInit, OnDestroy {
  protected isSurveyEnabled = false;
  protected surveyTitle: string | null = null;
  protected surveyDescription: string | null = null;
  protected surveyDisclaimer: string | null = null;
  protected submitButtonText: string | null = null;
  protected skipButtonText: string | null = null;
  protected questions: SurveyQuestion[] = [];
  protected newQuestionText = '';
  protected surveyResults: TSurveyResult[] = [];
  protected surveyResultsLoading = false;
  protected showResults = false;
  protected currentPage = 1;
  protected pageSize = 10;
  protected totalPages = 0;
  protected totalResults = 0;
  private _subs: Subscription[] = [];

  constructor(
    private _metadataService: MetadataService,
    private _commonService: CommonService,
    private _exitSurveyService: ExitSurveyService
  ) {}

  ngOnInit(): void {
    this.getSurveyEnabled();
    this.getSurveyTitle();
    this.getSurveyDescription();
    this.getSurveyDisclaimer();
    this.getButtonTexts();
    this.getQuestions();
  }

  getSurveyEnabled() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveyEnabled).subscribe({
        next: metadata => {
          this.isSurveyEnabled = metadata.value === 'true';
        },
        error: () => {
          this.isSurveyEnabled = false;
        },
      })
    );
  }

  getSurveyTitle() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveyTitle).subscribe({
        next: metadata => {
          this.surveyTitle = metadata.value || 'Quick Survey';
        },
        error: () => {
          this.surveyTitle = 'Quick Survey';
        },
      })
    );
  }

  getSurveyDescription() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveyDescription).subscribe({
        next: metadata => {
          this.surveyDescription = metadata.value || null;
        },
        error: () => {
          this.surveyDescription = null;
        },
      })
    );
  }

  getSurveyDisclaimer() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveyDisclaimer).subscribe({
        next: metadata => {
          this.surveyDisclaimer = metadata.value || null;
        },
        error: () => {
          this.surveyDisclaimer = null;
        },
      })
    );
  }

  getButtonTexts() {
    // Get submit button text
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveySubmitButtonText).subscribe({
        next: metadata => {
          this.submitButtonText = metadata.value || null;
        },
        error: () => {
          this.submitButtonText = null;
        },
      })
    );

    // Get skip button text
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveySkipButtonText).subscribe({
        next: metadata => {
          this.skipButtonText = metadata.value || null;
        },
        error: () => {
          this.skipButtonText = null;
        },
      })
    );
  }

  getQuestions() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ExitSurveyQuestions).subscribe({
        next: metadata => {
          if (metadata.value) {
            try {
              this.questions = JSON.parse(metadata.value);
            } catch (e) {
              this.questions = [];
            }
          } else {
            this.questions = [];
          }
        },
        error: () => {
          this.questions = [];
        },
      })
    );
  }

  onSurveyEnabledChange(event: MatSlideToggleChange) {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveyEnabled, event.checked.toString())
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Survey setting updated!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error updating survey setting');
          },
        })
    );
  }

  addQuestion() {
    if (this.newQuestionText.trim()) {
      const nextId =
        this.questions.length > 0
          ? Math.max(...this.questions.map(q => parseInt(q.id) || 0)) + 1
          : 0;

      const newQuestion: SurveyQuestion = {
        id: nextId.toString(),
        question: this.newQuestionText.trim(),
      };
      this.questions.push(newQuestion);
      this.newQuestionText = '';
      this.saveQuestions();
    }
  }

  deleteQuestion(questionId: string) {
    this.questions = this.questions.filter(q => q.id !== questionId);
    this.saveQuestions();
  }

  saveQuestions() {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveyQuestions, JSON.stringify(this.questions))
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Questions saved!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error saving questions');
          },
        })
    );
  }

  saveTitle() {
    this._subs.push(
      this._metadataService.set(MetadataSlugs.ExitSurveyTitle, this.surveyTitle).subscribe({
        next: () => {
          this._commonService.openSuccessSnackBar('Title saved!');
        },
        error: () => {
          this._commonService.openErrorSnackBar('Error saving title');
        },
      })
    );
  }

  saveDescription() {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveyDescription, this.surveyDescription)
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Description saved!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error saving description');
          },
        })
    );
  }

  saveDisclaimer() {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveyDisclaimer, this.surveyDisclaimer)
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Disclaimer saved!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error saving disclaimer');
          },
        })
    );
  }

  saveSubmitButtonText() {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveySubmitButtonText, this.submitButtonText)
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Submit button text saved!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error saving submit button text');
          },
        })
    );
  }

  saveSkipButtonText() {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.ExitSurveySkipButtonText, this.skipButtonText)
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Skip button text saved!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error saving skip button text');
          },
        })
    );
  }

  exportSurveyData() {
    this._exitSurveyService.exportSurveyData();
  }

  loadSurveyResults() {
    this.surveyResultsLoading = true;
    this._subs.push(
      this._exitSurveyService.getSurveyResults(this.pageSize, this.currentPage).subscribe({
        next: (response: TSurveyResultsResponse) => {
          this.surveyResults = response.results;
          this.totalResults = response.total;
          this.totalPages = response.totalPages;
          this.currentPage = response.currentPage;
          this.surveyResultsLoading = false;
          this.showResults = true;
        },
        error: error => {
          this._commonService.openErrorSnackBar('Error loading survey results');
          this.surveyResultsLoading = false;
          console.error('Error loading survey results:', error);
        },
      })
    );
  }

  toggleResults() {
    if (this.showResults) {
      this.showResults = false;
    } else {
      this.loadSurveyResults();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadSurveyResults();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadSurveyResults();
    }
  }

  getAverageRating(questionId: string): number {
    if (this.surveyResults.length === 0) return 0;

    const ratings = this.surveyResults
      .map(result => result.answers.find(answer => answer.questionId === questionId)?.rating)
      .filter(rating => rating !== undefined) as number[];

    if (ratings.length === 0) return 0;

    const sum = ratings.reduce((acc, rating) => acc + rating, 0);
    return Math.round((sum / ratings.length) * 10) / 10;
  }

  resetSurveySession() {
    this._exitSurveyService.resetSessionStatus();
    this._commonService.openSuccessSnackBar(
      'Survey session status reset - survey can appear again in this session'
    );
  }

  get hasBeenShownThisSession(): boolean {
    return this._exitSurveyService.hasBeenShownThisSession();
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
